import { useState } from 'react';
import { Info, AlertCircle, CheckCircle2, Eye } from 'lucide-react';
import { useApproveAction, useRejectAction } from '../../hooks/useActions';
import EmailListOverlay from '@/components/common/EmailListOverlay';

export default function ActionApprovalCard({ suggestion, onResolved }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailOverlay, setShowEmailOverlay] = useState(false);
  const approveAction = useApproveAction();
  const rejectAction = useRejectAction();

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await approveAction.mutateAsync({ id: suggestion.id, always: false });
      onResolved?.(suggestion.id);
    } catch (error) {
      console.error('Failed to approve action:', error);
      alert('Failed to approve action. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      await rejectAction.mutateAsync({ id: suggestion.id, never: false });
      onResolved?.(suggestion.id);
    } catch (error) {
      console.error('Failed to reject action:', error);
      alert('Failed to reject action. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const confidencePercent = suggestion.confidence
    ? Math.round(suggestion.confidence * 100)
    : null;

  const getActionIcon = () => {
    if (suggestion.action_name.includes('delete')) return <AlertCircle className="h-5 w-5 text-red-500" />;
    if (suggestion.action_name.includes('archive')) return <CheckCircle2 className="h-5 w-5 text-blue-500" />;
    return <Info className="h-5 w-5 text-blue-500" />;
  };

  const displayName = suggestion.action_name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

  return (
    <>
      <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3 mb-3">
          {getActionIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {displayName}
              </p>
              {confidencePercent && (
                <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                  {confidencePercent}% confident
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              {suggestion.reason}
            </p>

            {suggestion.email_ids && suggestion.email_ids.length > 1 && (
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Affects {suggestion.email_ids.length} emails
              </p>
            )}

            {suggestion.params && Object.keys(suggestion.params).length > 0 && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 font-mono">
                {Object.entries(suggestion.params).map(([key, value]) => (
                  <div key={key}>
                    {key}: {JSON.stringify(value)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleApprove}
            disabled={isProcessing}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded transition-colors"
          >
            Yes
          </button>
          <button
            onClick={handleReject}
            disabled={isProcessing}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:bg-gray-200 dark:disabled:bg-gray-800 rounded transition-colors"
          >
            No
          </button>
          {suggestion.email_ids && suggestion.email_ids.length > 0 && (
            <button
              onClick={() => setShowEmailOverlay(true)}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded transition-colors flex items-center gap-1"
            >
              <Eye className="h-3 w-3" />
              Show Emails
            </button>
          )}
        </div>
      </div>

      {showEmailOverlay && suggestion.email_ids && (
        <EmailListOverlay
          emailIds={suggestion.email_ids}
          title={`${displayName} — ${suggestion.email_ids.length} Email${suggestion.email_ids.length !== 1 ? 's' : ''}`}
          onClose={() => setShowEmailOverlay(false)}
        />
      )}
    </>
  );
}
